

import { useState } from "react"
import { Clock, ChevronDown, ChevronUp, CheckCircle2, XCircle, AlertTriangle, FileArchive } from "lucide-react"

import type { Attempt } from "@/types/processador-previdenciario"
import { cn } from "@/lib/utils"
import { StepResult } from "./step-result"

interface AttemptHistoryProps {
  attempts: Attempt[]
}

export function AttemptHistory({ attempts }: AttemptHistoryProps) {
  const [expandedId, setExpandedId] = useState<number | null>(null)

  if (attempts.length === 0) return null

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "success":
        return <CheckCircle2 className="h-3.5 w-3.5 text-success" />
      case "error":
        return <XCircle className="h-3.5 w-3.5 text-destructive" />
      case "warning":
        return <AlertTriangle className="h-3.5 w-3.5 text-warning" />
      default:
        return <Clock className="h-3.5 w-3.5 text-muted-foreground" />
    }
  }

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })
  }

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2 px-1">
        <Clock className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-xs font-medium text-muted-foreground">
          Historico de tentativas ({attempts.length})
        </span>
      </div>
      <div className="space-y-1">
        {attempts.map((attempt) => {
          const isExpanded = expandedId === attempt.id
          return (
            <div
              key={attempt.id}
              className={cn(
                "rounded-lg border transition-all",
                attempt.result.status === "success" && "border-success/20",
                attempt.result.status === "error" && "border-destructive/20",
                attempt.result.status === "warning" && "border-warning/20",
                !["success", "error", "warning"].includes(attempt.result.status) && "border-border"
              )}
            >
              <button
                type="button"
                onClick={() => setExpandedId(isExpanded ? null : attempt.id)}
                className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-accent/30 transition-colors rounded-lg"
              >
                {getStatusIcon(attempt.result.status)}
                <span className="text-xs font-medium text-foreground">
                  Tentativa #{attempt.id}
                </span>
                <span className="text-xs text-muted-foreground">
                  {formatTime(attempt.timestamp)}
                </span>

                {attempt.result.totalCount !== undefined && (
                  <span className="ml-auto text-xs text-muted-foreground">
                    {attempt.result.successCount ?? 0}
                    <span className="text-success">/</span>
                    {attempt.result.errorCount ?? 0}
                    <span className="text-destructive">/</span>
                    {attempt.result.warningCount ?? 0}
                  </span>
                )}

                {attempt.files && attempt.files.length > 0 && (
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <FileArchive className="h-3 w-3" />
                    {attempt.files.length}
                  </div>
                )}

                {isExpanded ? (
                  <ChevronUp className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                ) : (
                  <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                )}
              </button>

              {isExpanded && (
                <div className="px-3 pb-3">
                  {attempt.files && attempt.files.length > 0 && (
                    <div className="mb-2 flex flex-wrap gap-1">
                      {attempt.files.map((f, i) => (
                        <span
                          key={i}
                          className="inline-flex items-center gap-1 rounded-md bg-accent px-2 py-0.5 text-xs text-muted-foreground"
                        >
                          <FileArchive className="h-3 w-3" />
                          {f}
                        </span>
                      ))}
                    </div>
                  )}
                  {attempt.result.message && (
                    <p className="text-xs text-muted-foreground mb-2">{attempt.result.message}</p>
                  )}
                  {attempt.result.data && (
                    <StepResult data={attempt.result.data} stepName={`Tentativa #${attempt.id}`} />
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
